// frontend/client/src/components/EditStudio.js
// Unified Edit & Caption Studio — replaces the separate ContentLab.js and
// VideoTools.js routes.
//
// WHY THIS MERGE HAPPENED: both prior pages independently implemented their
// own full copy of AI Director (VideoTools' handleRunTriad + its own polling
// + its own toast messages, and ContentLab's applyViralCut toggle calling
// the exact same /video/process-triad endpoint) — genuinely duplicated
// logic, not just a UI overlap. Each page also had its own "export to the
// other page" button, meaning a real user workflow (edit a clip, then
// caption it) required bouncing between two pages that both tried to own
// the same video state independently. That's almost certainly the
// "VideoTools feels repetitive" complaint traced to its actual root cause.
//
// DESIGN PRINCIPLE (Maria's refined suggestion): manual tools and AI
// Director stay conceptually separate, but now live in one view instead of
// two pages, and AI Director appears exactly once — reframed around what it
// actually does that manual editing structurally can't (autonomously find
// and cut the hook), not as a duplicate "auto" button sitting next to the
// same "Apply Edits" button.

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { api } from '../services/apiClient';
import { useMinoStore } from '../store/useMinoStore';
import {
  Upload, Loader2, Play, FileVideo, Crop, Sparkles, Check, Menu, X,
  Wand2, Instagram, MessageCircle, Linkedin, Twitter, Copy, Bookmark,
  ArrowRight, Zap, Clock, Scissors
} from 'lucide-react';

const ASPECT_RATIOS = [
  { label: '9:16', value: '9:16', desc: 'Reels / TikTok' },
  { label: '4:5', value: '4:5', desc: 'Feed Portrait' },
  { label: '1:1', value: '1:1', desc: 'Feed Square' },
  { label: '16:9', value: '16:9', desc: 'YouTube / Web' },
];

const RATIO_TO_PLATFORM = {
  '9:16': 'instagram_reels',
  '4:5': 'instagram_feed_portrait',
  '1:1': 'instagram_feed',
  '16:9': 'youtube'
};

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C' },
  { id: 'tiktok', label: 'TikTok', icon: MessageCircle, color: '#000000' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { id: 'twitter', label: 'Twitter/X', icon: Twitter, color: '#000000' },
];

const TONES = ['casual', 'professional', 'playful', 'inspirational', 'educational', 'promotional'];

function splitCaptionAndHashtags(text) {
  if (!text) return { caption: '', hashtags: [] };
  const hashtags = (text.match(/#\w+/g) || []);
  const caption = text.replace(/#\w+/g, '').replace(/\s{2,}/g, ' ').trim();
  return { caption, hashtags };
}

const ES_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Syne:wght@400;600;700;800&display=swap');

  @keyframes es-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  .es-spin { animation: es-spin 1s linear infinite; }

  .es-root {
    display: flex;
    height: 100vh;
    background: var(--bg-canvas, #F8FAFC);
    font-family: var(--font-body, 'Plus Jakarta Sans', sans-serif);
    color: var(--text-main, #0F172A);
  }
  .es-sidebar {
    width: 260px;
    background: var(--bg-surface, #FFFFFF);
    border-right: 1px solid var(--border-light, #E2E8F0);
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex-shrink: 0;
  }
  .es-brand-title {
    font-family: var(--font-display, 'Syne', sans-serif);
    font-weight: 800;
    font-size: 22px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .es-sidebar-title {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--text-muted, #94A3B8); margin: 0 0 8px;
  }
  .es-library-scroll { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; }
  .es-library-item {
    display: flex; align-items: center; gap: 10px; padding: 10px;
    border-radius: 10px; cursor: pointer; transition: all 0.15s; border: 1px solid transparent;
  }
  .es-library-item:hover { background: var(--bg-canvas, #F8FAFC); }
  .es-library-item.active {
    background: var(--brand-secondary, #EEF2FF);
    border-color: var(--brand-primary, #4F46E5);
  }
  .es-library-thumb {
    width: 40px; height: 40px; background: #E2E8F0; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .es-upload-btn {
    width: 100%; padding: 12px; border-radius: 10px;
    border: 1px dashed var(--border-light, #CBD5E1);
    background: var(--bg-canvas, #F8FAFC); color: var(--text-main, #475569);
    font-weight: 600; font-size: 13px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    transition: all 0.2s;
  }
  .es-upload-btn:hover { border-color: var(--brand-primary, #4F46E5); color: var(--brand-primary, #4F46E5); }

  .es-main { flex: 1; display: flex; flex-direction: column; padding: 24px 28px; overflow-y: auto; min-width: 0; }
  .es-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 20px; flex-wrap: wrap; gap: 12px;
  }
  .es-video-title { font-family: var(--font-display, 'Syne', sans-serif); font-weight: 700; font-size: 18px; }
  .es-toggle-group { display: flex; background: var(--bg-canvas, #F1F5F9); border-radius: 8px; padding: 4px; }
  .es-toggle-btn {
    padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 600;
    cursor: pointer; border: none; background: transparent; color: var(--text-muted, #64748B);
    transition: all 0.2s; font-family: inherit;
  }
  .es-toggle-btn.active { background: #FFFFFF; color: var(--text-main, #0F172A); box-shadow: 0 1px 3px rgba(0,0,0,0.08); }

  .es-workspace { display: grid; grid-template-columns: 1fr 360px; gap: 20px; flex: 1; min-height: 0; }
  @media (max-width: 1100px) { .es-workspace { grid-template-columns: 1fr; } }

  /* Player deck */
  .es-player-card {
    background: var(--bg-surface, #FFFFFF); border: 1px solid var(--border-light, #E2E8F0);
    border-radius: 16px; padding: 16px; box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0,0,0,0.02));
    display: flex; flex-direction: column; gap: 16px;
  }
  .es-player-wrapper {
    flex: 1; display: flex; align-items: center; justify-content: center;
    background: #0F172A; border-radius: 12px; overflow: hidden; position: relative; min-height: 0;
  }
  .es-player-wrapper[data-ratio="9:16"] { aspect-ratio: 9/16; max-width: 320px; margin: 0 auto; }
  .es-player-wrapper[data-ratio="4:5"]  { aspect-ratio: 4/5;  max-width: 360px; margin: 0 auto; }
  .es-player-wrapper[data-ratio="1:1"]  { aspect-ratio: 1/1;  max-width: 400px; margin: 0 auto; }
  .es-player-wrapper[data-ratio="16:9"] { aspect-ratio: 16/9; max-width: 100%; margin: 0 auto; }
  .es-player-wrapper video { width: 100%; height: 100%; object-fit: contain; display: block; }
  .es-player-placeholder { display: flex; flex-direction: column; align-items: center; gap: 12px; color: #64748B; }

  /* The two decks living side by side, not two pages apart */
  .es-deck-tabs { display: flex; gap: 6px; margin-top: 4px; }
  .es-deck-tab {
    flex: 1; padding: 10px; border-radius: 10px; border: 1px solid var(--border-light, #E2E8F0);
    background: var(--bg-surface, #FFFFFF); font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px; font-family: inherit;
    color: var(--text-muted, #64748B); transition: all 0.15s;
  }
  .es-deck-tab.active { background: var(--brand-secondary, #EEF2FF); border-color: var(--brand-primary, #4F46E5); color: var(--brand-primary, #4F46E5); }

  /* Tools panel (right side) */
  .es-tools-panel { display: flex; flex-direction: column; gap: 16px; overflow-y: auto; }
  .es-tool-card {
    background: var(--bg-surface, #FFFFFF); border: 1px solid var(--border-light, #E2E8F0);
    border-radius: 16px; padding: 20px; box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0,0,0,0.02));
  }
  .es-tool-card-title {
    font-family: var(--font-display, 'Syne', sans-serif); font-weight: 700; font-size: 14px;
    margin: 0 0 14px; color: var(--text-main, #0F172A); display: flex; align-items: center; gap: 8px;
  }
  .es-switch-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 0; border-bottom: 1px solid var(--border-light, #F1F5F9);
  }
  .es-switch-row:last-child { border-bottom: none; padding-bottom: 0; }
  .es-switch-info h4 { margin: 0 0 4px; font-size: 13px; font-weight: 600; }
  .es-switch-info p { margin: 0; font-size: 11px; color: var(--text-muted, #64748B); }
  .es-ratio-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 12px; }
  .es-ratio-btn {
    border: 1px solid var(--border-light, #E2E8F0); background: var(--bg-canvas, #F8FAFC);
    padding: 10px 0; border-radius: 8px; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.2s; font-family: inherit;
  }
  .es-ratio-btn.active {
    background: var(--brand-secondary, #EEF2FF);
    border-color: var(--brand-primary, #4F46E5); color: var(--brand-secondary-contrast, var(--brand-primary, #4F46E5));
  }
  .es-ratio-btn small { display: block; font-size: 9px; font-weight: 500; color: var(--text-muted, #94A3B8); margin-top: 2px; }

  .es-apply-btn {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    width: 100%; padding: 14px; border-radius: 12px;
    background: linear-gradient(135deg, #4F46E5, #8B5CF6); color: white;
    font-size: 14px; font-weight: 700; border: none; cursor: pointer;
    font-family: inherit; box-shadow: 0 4px 14px rgba(79,70,229,0.25);
    transition: opacity 0.2s; margin-top: 4px;
  }
  .es-apply-btn:hover:not(:disabled) { opacity: 0.9; }
  .es-apply-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* The one, distinctive AI Director card — not a second "auto" toggle
     sitting next to manual switches, but its own clearly different action:
     it finds the hook autonomously, something scrubbing a slider can't do. */
  .es-director-card {
    background: linear-gradient(135deg, #FDF4FF, #EEF2FF);
    border: 1px solid #E9D5FF; border-radius: 16px; padding: 20px;
    display: flex; flex-direction: column; gap: 12px;
  }
  .es-director-card h4 { margin: 0; font-family: var(--font-display, 'Syne'); font-size: 14px; display: flex; align-items: center; gap: 6px; }
  .es-director-card p { margin: 0; font-size: 12px; color: #64748B; line-height: 1.5; }
  .es-director-btn {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px; border-radius: 10px; background: #0F172A; color: white;
    font-size: 13px; font-weight: 700; border: none; cursor: pointer;
    transition: background 0.2s;
  }
  .es-director-btn:hover:not(:disabled) { background: #334155; }
  .es-director-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .es-director-btn.secondary { background: transparent; color: #0F172A; border: 1px solid #E2E8F0; }
  .es-director-btn.secondary:hover { background: #F8FAFC; }

  /* Caption deck */
  .es-caption-textarea {
    width: 100%; min-height: 90px; padding: 12px 14px; border-radius: 12px;
    border: 1px solid var(--border-light, #E2E8F0); font-family: inherit; font-size: 14px;
    resize: vertical; outline: none; transition: border-color 0.15s; box-sizing: border-box;
  }
  .es-caption-textarea:focus { border-color: var(--brand-primary, #4F46E5); }
  .es-platform-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .es-platform-btn {
    display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 10px;
    border-radius: 10px; border: 2px solid var(--border-light, #E2E8F0); background: var(--bg-surface, #FFFFFF);
    cursor: pointer; transition: all 0.2s;
  }
  .es-platform-btn.active { border-color: var(--brand-primary, #4F46E5); background: var(--brand-secondary, #EEF2FF); }
  .es-platform-label { font-size: 11px; font-weight: 700; }
  .es-tone-grid { display: flex; flex-wrap: wrap; gap: 6px; }
  .es-tone-btn {
    padding: 6px 12px; border-radius: 9999px; border: 1px solid var(--border-light, #E2E8F0);
    background: var(--bg-surface, #FFFFFF); font-size: 11px; font-weight: 700; cursor: pointer;
    transition: all 0.15s; font-family: inherit;
  }
  .es-tone-btn.active { background: var(--brand-primary, #4F46E5); color: white; border-color: var(--brand-primary, #4F46E5); }
  .es-variant-card { padding: 14px; border-radius: 12px; border: 1px solid var(--border-light, #E2E8F0); background: var(--bg-surface, #FFFFFF); }
  .es-variant-text { font-size: 13px; line-height: 1.6; margin: 0 0 10px; white-space: pre-wrap; }
  .es-variant-actions { display: flex; gap: 8px; }
  .es-action-btn {
    display: flex; align-items: center; gap: 5px; padding: 7px 11px; border-radius: 8px;
    border: 1px solid var(--border-light, #E2E8F0); background: var(--bg-surface, #FFFFFF);
    font-size: 11px; font-weight: 700; cursor: pointer; transition: all 0.15s;
  }
  .es-action-btn:hover { border-color: var(--brand-primary, #4F46E5); color: var(--brand-primary, #4F46E5); }

  /* Toggle Switch */
  .switch { position: relative; display: inline-block; width: 44px; height: 24px; }
  .switch input { opacity: 0; width: 0; height: 0; }
  .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #CBD5E1; transition: .4s; border-radius: 34px; }
  .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; }
  input:checked + .slider { background-color: var(--brand-primary, #4F46E5); }
  input:checked + .slider:before { transform: translateX(20px); }

  .es-mobile-menu-btn { display: none; background: none; border: none; cursor: pointer; padding: 4px; color: var(--text-main); }
  .es-sidebar-close { display: none; background: none; border: none; cursor: pointer; padding: 4px; }
  .es-mobile-overlay { display: none; }

  @media (max-width: 1024px) {
    .es-root { flex-direction: column; height: auto; min-height: 100vh; }
    .es-sidebar {
      position: fixed; top: 0; left: 0; bottom: 0; z-index: 100;
      transform: translateX(-100%); transition: transform 0.3s ease;
      box-shadow: 4px 0 24px rgba(0,0,0,0.15);
    }
    .es-sidebar.open { transform: translateX(0); }
    .es-mobile-overlay { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(2px); z-index: 99; }
    .es-mobile-menu-btn { display: flex; align-items: center; justify-content: center; margin-right: 8px; }
    .es-sidebar-close { display: block; }
    .es-main { padding: 16px; }
    .es-workspace { grid-template-columns: 1fr; }
    .es-header { gap: 8px; }
    .es-video-title { font-size: 15px; }
  }
  .es-empty-state {
    flex: 1; display: flex; flex-direction: column; align-items: center;
    justify-content: center; color: var(--text-muted, #94A3B8); text-align: center; padding: 40px;
  }
`;

export default function EditStudio() {
  const { showToast } = useToast();
  const addToast = ({ message, type = 'success' } = {}) => showToast(message, type);
  const navigate = useNavigate();
  const location = useLocation();
  const preloadedMedia = location.state?.preloadedMedia || location.state?.video || null;
  const triggerMino = useMinoStore((s) => s.trigger);

  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // FIX: neither this file nor the original VideoTools.js ever implemented
  // real drag-and-drop — only a hidden file input behind a click. Dropping
  // a file onto a page with no drop handler falls back to the browser's
  // own default: opening the file directly in a new tab/window, exactly
  // what was reported. This state drives a real drop zone below.
  const [dragOver, setDragOver] = useState(false);

  const [viewMode, setViewMode] = useState('before');
  const [selectedRatio, setSelectedRatio] = useState('9:16');
  const [activeDeck, setActiveDeck] = useState('edit'); // 'edit' | 'caption'

  // Manual editing state (unchanged from VideoTools — same tools, same
  // underlying API calls, just relocated into one shared view)
  const [enhance, setEnhance] = useState(false);
  const [crop, setCrop] = useState(false);
  const [trim, setTrim] = useState(false);
  const [trimStart, setTrimStart] = useState('0');
  const [trimEnd, setTrimEnd] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  // AI Director state — now the ONLY copy of this logic in the app
  const [isDirectorRunning, setIsDirectorRunning] = useState(false);
  const [directorComplete, setDirectorComplete] = useState(false);
  const [directorMeta, setDirectorMeta] = useState(null);
  const directorPollRef = useRef(null);

  // Caption deck state (from ContentLab, already fixed to use the real api client)
  const [prompt, setPrompt] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('casual');
  const [generating, setGenerating] = useState(false);
  const [variants, setVariants] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    setLoadingVideos(true);
    api.get('/video/my-videos')
      .then(res => {
        if (res.success && res.videos?.length) {
          setVideos(res.videos);
          if (preloadedMedia) {
            const match = res.videos.find(v => v._id === preloadedMedia._id || v.originalVideo?.publicId === preloadedMedia.originalVideo?.publicId);
            setSelectedVideo(match || preloadedMedia);
            if (!match) setVideos(prev => [preloadedMedia, ...prev]);
          } else {
            setSelectedVideo(res.videos[0]);
          }
        } else if (preloadedMedia) {
          setVideos([preloadedMedia]);
          setSelectedVideo(preloadedMedia);
        }
      })
      .finally(() => setLoadingVideos(false));
  }, [preloadedMedia]);

  useEffect(() => {
    return () => { if (directorPollRef.current) clearInterval(directorPollRef.current); };
  }, []);

  const handleUpload = useCallback(async (files) => {
    if (!files?.length) return;
    const file = files[0];
    if (!file.type.startsWith('video/')) return addToast({ type: 'error', message: 'That needs to be a video file' });

    const MAX_MB = 100;
    if (file.size > MAX_MB * 1024 * 1024) {
      return addToast({ type: 'error', message: `That's a bit big — keep it under ${MAX_MB}MB for now` });
    }

    setUploading(true);
    triggerMino('thinking', 'Bringing your video in...', file.name);
    try {
      const sigRes = await api.post('/media/signature', { resource_type: 'video', folder: 'infleuince/videos' });
      if (!sigRes.success) throw new Error(sigRes.error);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sigRes.apiKey);
      formData.append('timestamp', sigRes.timestamp);
      formData.append('signature', sigRes.signature);
      // FIX: this fell back to a hardcoded folder if sigRes.folder was ever
      // empty — but Cloudinary's signature is computed from the exact
      // params sent to /signature. If the backend ever signed a request
      // with no folder (e.g. an empty request body) while this still sent
      // one anyway, the signature and the actual upload params diverge,
      // and Cloudinary rejects the whole request with 401. Only append
      // folder when the signing response actually included one, so what
      // gets uploaded can never disagree with what was signed.
      if (sigRes.folder) formData.append('folder', sigRes.folder);

      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${sigRes.cloudName}/video/upload`, { method: 'POST', body: formData });
      const cloudData = await cloudRes.json();
      if (!cloudRes.ok) throw new Error(cloudData.error?.message);

      const registerRes = await api.post('/video/register', {
        publicId: cloudData.public_id, secureUrl: cloudData.secure_url, name: file.name, duration: cloudData.duration,
      });

      const newVideo = registerRes.success ? registerRes.video : {
        _id: cloudData.public_id, name: file.name, status: 'ready', originalVideo: { secure_url: cloudData.secure_url, duration: cloudData.duration }
      };

      setVideos(prev => [newVideo, ...prev]);
      setSelectedVideo(newVideo);
      setViewMode('before');
      setDirectorComplete(false);
      setTrim(false);
      setTrimStart('0');
      setTrimEnd(cloudData.duration ? String(Math.floor(cloudData.duration)) : '');
      addToast({ type: 'success', message: "It's in — let's make something." });
      triggerMino('success', 'Got it!', 'Ready when you are.');
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'That upload didn\'t make it — try again?' });
    }
    setUploading(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  const handleApply = async () => {
    if (!selectedVideo) return;
    if (!enhance && !crop && !trim) {
      addToast({ type: 'warning', message: 'Pick at least one thing to do first' });
      return;
    }

    setIsProcessing(true);
    setViewMode('after');
    triggerMino('thinking', 'Working on it…', 'One sec');

    try {
      let currentUrl = selectedVideo.originalVideo?.secure_url;

      if (crop) {
        setProcessingMessage('Resizing…');
        const res = await api.post('/video/smart-crop', { videoId: selectedVideo._id, aspectRatio: selectedRatio });
        if (res.success) currentUrl = res.url;
      }
      if (trim) {
        setProcessingMessage('Trimming…');
        const res = await api.post('/video/trim', {
          videoId: selectedVideo._id,
          startOffset: parseFloat(trimStart) || 0,
          endOffset: trimEnd !== '' ? parseFloat(trimEnd) : undefined,
        });
        if (res.success) currentUrl = res.url;
        else addToast({ type: 'error', message: res.error || "Trim didn't go through" });
      }
      if (enhance) {
        setProcessingMessage('Enhancing…');
        const res = await api.post('/video/enhance', { videoId: selectedVideo._id });
        if (res.success) currentUrl = res.url;
      }

      setSelectedVideo(prev => ({ ...prev, processedUrl: currentUrl, status: 'ready' }));
      setVideos(prev => prev.map(v => v._id === selectedVideo._id ? { ...v, processedUrl: currentUrl, status: 'ready' } : v));

      addToast({ type: 'success', message: 'Done — take a look.' });
      triggerMino('success', 'Looking good!', 'Check out the after.');
    } catch (err) {
      addToast({ type: 'error', message: err.message });
      triggerMino('error', "That didn't work", err.message);
    }
    setIsProcessing(false);
    setProcessingMessage('');
  };

  // ── AI Director — the single, deduplicated implementation ────────────────
  const handleRunDirector = async () => {
    if (!selectedVideo) return;
    setIsDirectorRunning(true);
    setDirectorComplete(false);
    triggerMino('thinking', 'Finding your hook...', 'This takes a couple minutes');

    try {
      const res = await api.post('/video/process-triad', {
        videoId: selectedVideo._id,
        platform: RATIO_TO_PLATFORM[selectedRatio] || 'instagram_reels',
      });
      if (res.success) {
        addToast({ type: 'success', message: "On it — I'll find your best moment." });
        startDirectorPolling(selectedVideo._id);
      } else {
        addToast({ type: 'error', message: res.error || "Couldn't get AI Director started" });
        setIsDirectorRunning(false);
      }
    } catch (err) {
      addToast({ type: 'error', message: err?.response?.data?.error || err.message || "Couldn't get AI Director started" });
      setIsDirectorRunning(false);
    }
  };

  const startDirectorPolling = (videoId) => {
    if (directorPollRef.current) clearInterval(directorPollRef.current);
    directorPollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/video/${videoId}/status`);
        if (!res.success) return;

        if (res.status === 'ready' && res.url) {
          clearInterval(directorPollRef.current);
          directorPollRef.current = null;
          setIsDirectorRunning(false);
          setDirectorComplete(true);
          setDirectorMeta(res.directorData || null);

          setSelectedVideo(prev => ({ ...prev, processedUrl: res.url, captionedVideoUrl: res.url, status: 'ready' }));
          setVideos(prev => prev.map(v => v._id === videoId ? { ...v, processedUrl: res.url, captionedVideoUrl: res.url, status: 'ready' } : v));

          // FIX: the result was ready, but nothing switched the visible
          // view to show it — a user had to already know to click "After"
          // themselves, or discover it by navigating to Feed Preview.
          // Switching automatically means the moment AI Director finishes
          // is actually visible, not silent.
          setViewMode('after');
          setActiveDeck('caption');

          addToast({ type: 'success', message: 'Found it — your cut is ready.' });
          triggerMino('success', 'Hook found!', 'Take a look at your cut.');
        } else if (res.error) {
          clearInterval(directorPollRef.current);
          directorPollRef.current = null;
          setIsDirectorRunning(false);
          addToast({ type: 'error', message: `AI Director hit a snag: ${res.error}` });
          triggerMino('error', 'AI Director failed', res.error);
        }
      } catch (pollErr) {
        console.error('[Director Poll] Error:', pollErr);
      }
    }, 3000);
  };

  // ── Caption generation — reusing the already-fixed real api client call ──
  const handleGenerate = async () => {
    if (!prompt.trim() && !selectedVideo) {
      addToast({ type: 'warning', message: 'Tell me what this is about, or pick a video' });
      return;
    }
    setGenerating(true);
    setVariants([]);
    triggerMino('thinking', 'Writing your captions...', 'One sec');

    try {
      const res = await api.post('/captions/generate-caption-variants', { prompt, platform, tone });
      if (res.success) {
        setVariants(res.variants.map(v => ({ ...v, platform, tone })));
        addToast({ type: 'success', message: 'Here you go!' });
        triggerMino('success', 'Done!', 'Take a look below.');
      } else {
        throw new Error(res.error || 'Caption generation failed');
      }
    } catch (err) {
      addToast({ type: 'error', message: err.message || "That didn't work" });
      triggerMino('error', "Couldn't write those", 'Try again?');
    }
    setGenerating(false);
  };

  const handleCopy = (text, i) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(i);
    addToast({ type: 'success', message: 'Copied!' });
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleSave = async (variant) => {
    const { caption, hashtags } = splitCaptionAndHashtags(variant.caption);
    try {
      const res = await api.post('/captions', {
        caption, platform: variant.platform, tone: variant.tone, hashtags,
        originalPrompt: prompt, videoId: selectedVideo?._id || null,
        mediaUrl: selectedVideo?.processedUrl || selectedVideo?.originalVideo?.secure_url || null,
        mediaType: selectedVideo ? 'VIDEO' : 'IMAGE'
      });
      if (res.success) addToast({ type: 'success', message: 'Saved to your library.' });
      else throw new Error(res.error);
    } catch {
      addToast({ type: 'error', message: "Couldn't save that" });
    }
  };

  const handleSendToFeedPreview = (variant) => {
    navigate('/dashboard/preview', {
      state: {
        video: selectedVideo,
        processedUrl: selectedVideo?.processedUrl || selectedVideo?.originalVideo?.secure_url,
        caption: variant?.caption || directorMeta?.socialCaption || '',
        hashtags: variant?.hashtags || directorMeta?.hashtags || [],
        platform: variant?.platform || 'instagram'
      }
    });
  };

  const currentPreviewUrl = viewMode === 'before'
    ? selectedVideo?.originalVideo?.secure_url
    : (selectedVideo?.processedUrl || selectedVideo?.originalVideo?.secure_url);

  return (
    <div className="es-root">
      <style>{ES_CSS}</style>

      {sidebarOpen && <div className="es-mobile-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className={`es-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div className="es-brand-title"><Sparkles size={22} color="#4F46E5" /> Infleuince</div>
          <button className="es-sidebar-close" onClick={() => setSidebarOpen(false)}><X size={18} color="#94A3B8" /></button>
        </div>
        <div>
          <p className="es-sidebar-title">Your Videos</p>
          <div className="es-library-scroll">
            {loadingVideos ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                <Loader2 size={18} className="es-spin" color="#94A3B8" />
              </div>
            ) : (
              videos.map(v => (
                <div
                  key={v._id}
                  className={`es-library-item ${selectedVideo?._id === v._id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedVideo(v);
                    setDirectorComplete(false);
                    setViewMode('before');
                    setTrim(false);
                    setTrimStart('0');
                    setTrimEnd(v.originalVideo?.duration ? String(Math.floor(v.originalVideo.duration)) : '');
                    setSidebarOpen(false);
                  }}
                >
                  <div className="es-library-thumb"><Play size={14} color={selectedVideo?._id === v._id ? '#4F46E5' : '#94A3B8'} /></div>
                  <div style={{ overflow: 'hidden' }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{v.name}</p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>{v.status}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <button className="es-upload-btn" onClick={() => document.getElementById('es-file-upload').click()}>
          {uploading ? <><Loader2 size={14} className="es-spin" /> Bringing it in…</> : <><Upload size={14} /> Drop a video in</>}
        </button>
        <input id="es-file-upload" type="file" accept="video/*" style={{ display: 'none' }} onChange={e => handleUpload(e.target.files)} />
      </div>

      <div
        className="es-main"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={dragOver ? { outline: '2px dashed #4F46E5', outlineOffset: -8, background: 'var(--brand-secondary, #EEF2FF)' } : undefined}
      >
        <button className="es-mobile-menu-btn" style={{ alignSelf: 'flex-start', marginBottom: selectedVideo ? 0 : 12 }} onClick={() => setSidebarOpen(true)}>
          <Menu size={20} />
        </button>

        {selectedVideo ? (
          <>
            <div className="es-header">
              <div className="es-video-title">{selectedVideo.name}</div>
              <div className="es-toggle-group">
                <button className={`es-toggle-btn ${viewMode === 'before' ? 'active' : ''}`} onClick={() => setViewMode('before')}>Before</button>
                <button className={`es-toggle-btn ${viewMode === 'after' ? 'active' : ''}`} onClick={() => setViewMode('after')}>After</button>
              </div>
            </div>

            <div className="es-workspace">
              <div className="es-player-card">
                <div className="es-player-wrapper" data-ratio={selectedRatio}>
                  {currentPreviewUrl ? (
                    <video key={currentPreviewUrl} src={currentPreviewUrl} controls autoPlay loop playsInline />
                  ) : (
                    <div className="es-player-placeholder"><FileVideo size={40} color="#475569" /><span style={{ fontSize: 13 }}>Nothing to preview yet</span></div>
                  )}
                  {(isProcessing || isDirectorRunning) && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', backdropFilter: 'blur(4px)' }}>
                      <Loader2 size={36} className="es-spin" style={{ marginBottom: 12 }} />
                      <h4 style={{ margin: '0 0 6px', fontFamily: 'Syne', fontSize: 16 }}>{isDirectorRunning ? 'AI Director is on it' : 'Working on it'}</h4>
                      <p style={{ fontSize: 12, opacity: 0.8, margin: 0 }}>{isDirectorRunning ? 'Finding your hook and cutting captions in...' : (processingMessage || 'One sec')}</p>
                    </div>
                  )}
                </div>

                <div className="es-deck-tabs">
                  <button className={`es-deck-tab ${activeDeck === 'edit' ? 'active' : ''}`} onClick={() => setActiveDeck('edit')}>
                    <Scissors size={14} /> Edit
                  </button>
                  <button className={`es-deck-tab ${activeDeck === 'caption' ? 'active' : ''}`} onClick={() => setActiveDeck('caption')}>
                    <Wand2 size={14} /> Caption
                  </button>
                </div>
              </div>

              {/* FIX: the caption deck used to render inside the player card,
                  below the video — meaning clicking "Caption" pushed content
                  underneath instead of beside it, inconsistent with how the
                  Edit deck's tools panel already sits to the right. Now both
                  decks render in the same right-column position, matching
                  the pattern the video editor already established. */}
              {activeDeck === 'caption' && (
                <div className="es-tools-panel">
                  {/* FIX: this was the actual reason the result was invisible
                      inside EditStudio — directorMeta was only ever read as
                      a fallback when sending to Feed Preview, never shown
                      here at all. This card also gives AI Director's output
                      its own distinct visual identity, separate from a
                      manually-typed caption, directly addressing the
                      "can't tell them apart" problem. */}
                  {directorMeta?.socialCaption && (
                    <div className="es-director-card">
                      <h4><Zap size={14} /> What AI Director found</h4>
                      <p style={{ color: '#1E1B29', fontWeight: 600 }}>{directorMeta.socialCaption}</p>
                      <p style={{ fontSize: 11 }}>Written from watching your video, not a typed prompt — the hook and caption below both came from the same analysis.</p>
                    </div>
                  )}

                  <div className="es-tool-card">
                    <h3 className="es-tool-card-title"><Wand2 size={16} color="#4F46E5" /> Write a Caption</h3>
                    <textarea
                      className="es-caption-textarea"
                      placeholder="What's this video about?"
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                    />
                  </div>

                  <div className="es-tool-card">
                    <h3 className="es-tool-card-title">Platform</h3>
                    <div className="es-platform-grid">
                      {PLATFORMS.map(p => (
                        <button key={p.id} className={`es-platform-btn ${platform === p.id ? 'active' : ''}`} onClick={() => setPlatform(p.id)}>
                          <p.icon size={16} color={p.color} />
                          <span className="es-platform-label">{p.label}</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 14 }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>TONE</p>
                      <div className="es-tone-grid">
                        {TONES.map(t => (
                          <button key={t} className={`es-tone-btn ${tone === t ? 'active' : ''}`} onClick={() => setTone(t)}>{t}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button className="es-apply-btn" onClick={handleGenerate} disabled={generating}>
                    {generating ? <><Loader2 size={16} className="es-spin" /> Writing…</> : <><Sparkles size={16} /> Write My Caption</>}
                  </button>

                  {variants.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {variants.map((v, i) => (
                        <div key={i} className="es-variant-card">
                          <p className="es-variant-text">{v.caption}</p>
                          <div className="es-variant-actions">
                            <button className="es-action-btn" onClick={() => handleCopy(v.caption, i)}>
                              {copiedIndex === i ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                            </button>
                            <button className="es-action-btn" onClick={() => handleSave(v)}><Bookmark size={12} /> Save</button>
                            <button className="es-action-btn" onClick={() => handleSendToFeedPreview(v)}>Preview <ArrowRight size={12} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Manual tools — pure, deterministic, user-directed. AI
                  Director lives separately below, never here, so the two
                  never compete for the same "which button do I press" moment. */}
              {activeDeck === 'edit' && (
                <div className="es-tools-panel">
                  <div className="es-tool-card">
                    <h3 className="es-tool-card-title"><Wand2 size={16} color="#4F46E5" /> Polish</h3>
                    <div className="es-switch-row">
                      <div className="es-switch-info"><h4>Enhance</h4><p>Sharpen, brighten, color-correct</p></div>
                      <label className="switch"><input type="checkbox" checked={enhance} onChange={() => setEnhance(v => !v)} /><span className="slider" /></label>
                    </div>
                  </div>

                  <div className="es-tool-card">
                    <h3 className="es-tool-card-title"><Crop size={16} color="#4F46E5" /> Resize</h3>
                    <div className="es-ratio-grid">
                      {ASPECT_RATIOS.map(r => (
                        <button key={r.value} className={`es-ratio-btn ${selectedRatio === r.value ? 'active' : ''}`} onClick={() => { setSelectedRatio(r.value); setCrop(true); }}>
                          {r.label}<small>{r.desc}</small>
                        </button>
                      ))}
                    </div>
                    <div className="es-switch-row" style={{ marginTop: 10, paddingBottom: 0 }}>
                      <div className="es-switch-info"><h4>Apply crop</h4><p>Resize to the ratio above</p></div>
                      <label className="switch"><input type="checkbox" checked={crop} onChange={() => setCrop(v => !v)} /><span className="slider" /></label>
                    </div>
                  </div>

                  <div className="es-tool-card">
                    <h3 className="es-tool-card-title"><Clock size={16} color="#4F46E5" /> Trim</h3>
                    <div className="es-switch-row" style={{ paddingTop: 0 }}>
                      <div className="es-switch-info">
                        <h4>Cut to a range</h4>
                        <p>{selectedVideo?.originalVideo?.duration ? `${Math.round(selectedVideo.originalVideo.duration)}s total` : 'Enter start/end in seconds'}</p>
                      </div>
                      <label className="switch"><input type="checkbox" checked={trim} onChange={() => setTrim(v => !v)} /><span className="slider" /></label>
                    </div>
                    {trim && (
                      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Start (s)</label>
                          <input type="number" min="0" value={trimStart} onChange={e => setTrimStart(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>End (s)</label>
                          <input type="number" min="0" value={trimEnd} onChange={e => setTrimEnd(e.target.value)} placeholder={selectedVideo?.originalVideo?.duration ? String(Math.round(selectedVideo.originalVideo.duration)) : 'end'} style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  <button className="es-apply-btn" onClick={handleApply} disabled={isProcessing || isDirectorRunning || (!enhance && !crop && !trim)}>
                    {isProcessing ? <><Loader2 size={16} className="es-spin" /> {processingMessage || 'Working…'}</> : <><Sparkles size={16} /> Apply Edits</>}
                  </button>

                  {/* The one AI Director surface in the entire app now */}
                  <div className="es-director-card">
                    <h4><Zap size={14} /> Let AI find your hook</h4>
                    <p>This isn't the same as the tools above — you're not picking a crop or a trim point. AI Director watches the whole clip, finds your strongest 10–15s moment on its own, and cuts straight to it with captions burned in.</p>
                    <button className="es-director-btn" onClick={handleRunDirector} disabled={isDirectorRunning || isProcessing}>
                      {isDirectorRunning ? <><Loader2 size={14} className="es-spin" /> Finding it…</> : directorComplete ? <><Check size={14} /> Hook found</> : <><Sparkles size={14} /> Run AI Director</>}
                    </button>
                    {directorComplete && (
                      <button className="es-director-btn secondary" onClick={() => handleSendToFeedPreview(null)}>Preview in Feed <ArrowRight size={14} /></button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="es-empty-state">
            <FileVideo size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
            <h2 style={{ fontFamily: 'Syne', color: 'var(--text-main)', margin: '0 0 8px', fontSize: 20 }}>Nothing here yet</h2>
            <p style={{ margin: '0 0 20px', fontSize: 14 }}>Drop a video in and let's get to work.</p>
            <button className="es-upload-btn" style={{ width: 'auto', padding: '12px 24px' }} onClick={() => document.getElementById('es-file-upload').click()}>
              {uploading ? <><Loader2 size={14} className="es-spin" /> Bringing it in…</> : <><Upload size={14} /> Drop a video in</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}